import type { OnboardingStep } from "~vue/CalendarFirstOnboarding/types";
import { i18n } from "~vue/i18n";

/**
 * Wording for the one persistent live region each host of these steps renders (LAB-1447).
 *
 * A live region inserted into the DOM alongside its text is not reliably announced — the region has to
 * already exist for the change to be observed. So every host keeps a single region mounted for the whole
 * flow and changes only its text, and the screens themselves carry no live markup. Sharing the wording here
 * keeps the linear flow and guided setup saying the same thing about the same screen.
 */

/**
 * Which screen of a step is showing. Several steps host both a wait and its result, and a screen reader user
 * who hears nothing when the result lands has no way to know the pipeline finished. `stalled` is the same
 * problem at the other end of a wait: the pipeline has outrun its budget and the screen grows a way past it.
 */
export type ScreenPhase = "waiting" | "stalled" | "result" | "leaving" | "error";

/* Translated per call rather than at module load, so the hosts' computed statuses follow the active locale. */
export function screenStatusFor(step: OnboardingStep, phase: ScreenPhase): string {
    const { t } = i18n.global;

    /* Step names, not positions: the progress bar already communicates position, and several steps share a
       segment. */
    const stepLabels: Record<OnboardingStep, string> = {
        identity: t("onboarding.calendarFirst.status.identity"),
        intro: t("onboarding.calendarFirst.status.intro"),
        context_rings: t("onboarding.calendarFirst.status.contextRings"),
        calendar_connect: t("onboarding.calendarFirst.status.calendarConnect"),
        scanning: t("onboarding.calendarFirst.status.scanning"),
        calendar_payoff: t("onboarding.calendarFirst.status.calendarPayoff"),
        assessment_intro: t("onboarding.calendarFirst.status.assessmentIntro"),
        assessment: t("onboarding.calendarFirst.status.assessment"),
        perspective_result: t("onboarding.calendarFirst.status.perspectiveResult"),
        sharpened: t("onboarding.calendarFirst.status.sharpened"),
        profile_intro: t("onboarding.calendarFirst.status.profileIntro"),
        profile: t("onboarding.calendarFirst.status.profile"),
        done: t("onboarding.calendarFirst.status.leaving"),
    };

    /* What each wait is for, so a wait and the result that replaces it never announce the same words. Steps
       absent from here have no waiting screen. */
    const waitingLabels: Partial<Record<OnboardingStep, string>> = {
        scanning: t("onboarding.calendarFirst.status.scanningWait"),
        perspective_result: t("onboarding.calendarFirst.status.perspectiveResultWait"),
        sharpened: t("onboarding.calendarFirst.status.sharpenedWait"),
        profile: t("onboarding.calendarFirst.status.profileWait"),
    };

    /* Waits whose overrun is silent otherwise: the escape hatch simply appears. The steps absent from here
       either offer no escape hatch or swap to their result screen on timeout, which announces itself. */
    const stalledLabels: Partial<Record<OnboardingStep, string>> = {
        scanning: t("onboarding.calendarFirst.status.scanningStalled"),
        profile: t("onboarding.calendarFirst.status.profileStalled"),
    };

    if (phase === "error") {
        return t("onboarding.calendarFirst.status.error");
    }
    if (phase === "leaving") {
        return t("onboarding.calendarFirst.status.leaving");
    }
    if (phase === "stalled") {
        return stalledLabels[step] ?? waitingLabels[step] ?? stepLabels[step];
    }
    if (phase === "waiting") {
        return waitingLabels[step] ?? stepLabels[step];
    }
    return stepLabels[step];
}
